<template>
    <div class="file-wrapper-multi-view-tabs">
        <ResizeObserver @notify="_resizeHandler" />
        <div ref="tabsContent" class="file-wrapper-multi-view-tabs__content">
            <div
                v-for="(item, index) in list"
                :key="index"
                :ref="(el) => getTabNode(el, index)"
                class="file-wrapper-multi-view-tabs__item"
                :class="{
                    'active': item.round === value
                }"
                :title="item.name"
                @click="onSelectTab(item.round, index)"
            >
                <div class="file-wrapper-multi-view-tabs__item__title">
                    {{ item.name }}
                </div>
                <template v-if="more">
                    <div class="file-wrapper-multi-view-tabs__item__timeline">
                        <span class="file-wrapper-multi-view-tabs__item__timeline__line" />
                        <span class="file-wrapper-multi-view-tabs__item__timeline__circle" />
                    </div>
                    <div class="file-wrapper-multi-view-tabs__item__date">
                        {{ item.date }}
                    </div>
                </template>
            </div>
        </div>
        <template v-if="isHasScrollbar">
            <div v-if="isHasPrev" class="file-wrapper-multi-view-tabs__prev">
                <PtIcon icon="SolidCaretCircleLeft" @click="onScrollTabs('prev')" />
            </div>
            <div v-if="isHasNext" class="file-wrapper-multi-view-tabs__next">
                <PtIcon icon="SolidCaretCircleRight" @click="onScrollTabs('next')" />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { defineProps, ref, nextTick, onMounted, defineEmits } from 'vue';
import type { Ref } from 'vue';
import { Icon as PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/CaretCircleLeft.svg';
import '@patsnap-ui/icon/assets/solid/CaretCircleRight.svg';
import { ResizeObserver } from 'vue-resize';
import _ from 'lodash';

defineProps({
    more: {
        type: Boolean,
        default: false,
    },
    list: {
        type: Array,
        default: () => [],
    },
    value: {
        type: Number,
        default: 1,
    },
});

const emit = defineEmits(['input']);


const isHasScrollbar = ref(false);
const isHasPrev = ref(false);
const isHasNext = ref(false);

const hasScrollButton = (content: HTMLElement) => {
    isHasPrev.value = content.scrollLeft > 0;
    // 可能有小数点误差，所以向上取整。
    isHasNext.value = Math.ceil(content.scrollLeft) < content.scrollWidth - content.clientWidth;
};

const tabsContent:Ref<HTMLElement | undefined> = ref();
const onScrollTabs = (type) => {
    if (tabsContent.value) {
        const currentScrollLeft = tabsContent.value.scrollLeft;
        const distance = type === 'prev' ? -200 : 200;
        const newScrollLeft = currentScrollLeft + distance;
        $(tabsContent.value).animate({
            scrollLeft: newScrollLeft,
        }, 200);
        hasScrollButton(tabsContent.value);
    }
};

const resizeHandler = async () => {
    await nextTick();
    if (tabsContent.value) {
        // scrollWidth是元素的整体宽度。
        // client是可视的宽度
        isHasScrollbar.value = tabsContent.value.scrollWidth > tabsContent.value.clientWidth;
        if (isHasScrollbar.value) {
            hasScrollButton(tabsContent.value);
        }
    }
};

const _resizeHandler = _.debounce(async () => {
    await nextTick();
    if (tabsContent.value) {
        isHasScrollbar.value = tabsContent.value.scrollWidth > tabsContent.value.clientWidth;
        if (isHasScrollbar.value) {
            hasScrollButton(tabsContent.value);
        }
    }
}, 100);

const tabNode = ref<Map<number, any>>(new Map());
const getTabNode = (el:any, id:number) => {
    if (el) {
        tabNode.value.set(id, el);
    }
};

const onSelectTab = (round, index) => {
    window.rvTrack('Change File Wrapper Multi View Round');
    const currentTab = tabNode.value.get(index);
    const tabRect = currentTab?.getBoundingClientRect();

    // 元素不完全在窗口内,则向右滚动citation-map-charts__container
    const tabContainer = document.querySelector('.file-wrapper-multi-view-tabs');
    // @ts-ignore
    const tabRectOffsetWidthRight = _.get(tabRect, 'right', 0) - tabContainer?.offsetWidth || 0;
    const scrollContainer = document.querySelector('.file-wrapper-multi-view-tabs__content');

    if (tabRectOffsetWidthRight > 0) {
        if (scrollContainer) {
            // 加50是+号和数字
            scrollContainer.scrollLeft = scrollContainer.scrollLeft + tabRectOffsetWidthRight + 50;
        }
    }

    const tabRectOffsetWidthLeft = _.get(tabRect, 'left', 0);
    if (tabRectOffsetWidthLeft < 0) {
        if (scrollContainer) {
            // 加50是+号和数字
            scrollContainer.scrollLeft = scrollContainer.scrollLeft + tabRectOffsetWidthLeft - 50;
        }
    }
    emit('input', round);
};

onMounted(async () => {
    resizeHandler();
});

</script>

<style lang="scss">
.file-wrapper-multi-view-tabs {
    padding: 0 16px;
    position: relative;
    .file-wrapper-multi-view-tabs__content {
        display: flex;
        //overflow: hidden;
        overflow-x: auto;
        overflow-y: hidden;

        // 隐藏滚动条
        &::-webkit-scrollbar {
            display: none;
        }
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .file-wrapper-multi-view-tabs__item {
        width: 212px;
        cursor: pointer;
        border-radius: 4px 4px 0 0;
        border-bottom: 2px solid transparent;
        padding: 8px 0 6px 0;
        flex-shrink: 0;
        .file-wrapper-multi-view-tabs__item__title {
            white-space:nowrap;
            overflow:hidden;
            text-overflow:ellipsis;
            height: 20px;
            line-height: 20px;
            //text-align: center;
            padding: 0 4px 0 32px;

            color: $text-secondary;
        }

        .file-wrapper-multi-view-tabs__item__timeline {
            margin: 4px 0;
            height: 8px;
            position: relative;
            .file-wrapper-multi-view-tabs__item__timeline__circle {
                position: absolute;
                top: 0;
                left: 32px;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                border: 1px solid $border-normal;
                background: #fff;
            }
            .file-wrapper-multi-view-tabs__item__timeline__line {
                position: absolute;
                width: 100%;
                top: 50%;
                left: 40px;
                right: 0;
                height: 1px;
                background: $border-normal;
                transform: translateY(-50%);
            }
        }

        .file-wrapper-multi-view-tabs__item__date {
            padding: 0 32px;
            font-size: 12px;
            line-height: 16px;
            color: $text-hint;
        }

        .time-line-enter {
            opacity: 0;
        }
        .time-line-enter-active {
            transition: opacity 0.2s;
        }
        .time-line-enter-to {
            opacity: 1;
        }

        .multi-view-show-leave {
            opacity: 1;
        }

        .time-line-leave-active {
            transition: opacity 0.2s;
        }

        .multi-view-show-leave-to {
            opacity: 0;
        }

        &:not(:first-child) {
            margin-left: 8px;
        }
        &:last-child {
            .file-wrapper-multi-view-tabs__item__timeline__line {
                display: none;
            }
        }
        &:hover {
            background: $bg-normal;
            border-color: $border-normal;
        }
        &.active {
            background: $green-light;
            border-color: $green-primary;
            .file-wrapper-multi-view-tabs__item__title,
            .file-wrapper-multi-view-tabs__item__date{
                color: $green-text;
            }
            .file-wrapper-multi-view-tabs__item__title {
                font-weight: 600;
            }
            .file-wrapper-multi-view-tabs__item__timeline__circle {
                background: $green-primary;
            }
        }
    }

    .file-wrapper-multi-view-tabs__prev,
    .file-wrapper-multi-view-tabs__next{
        position: absolute;
        height: 100%;
        width: 36px;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        .patsnap-biz-icon {
            cursor: pointer;
        }
    }
    .file-wrapper-multi-view-tabs__prev {
        left: 0;
        &:after {
            content: '';
            position: absolute;
            height: 100%;
            width: 100%;
            top: 0;
            right: -100%;
            background: linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
        }
    }
    .file-wrapper-multi-view-tabs__next {
        right: 0;
        &:after {
            content: '';
            position: absolute;
            height: 100%;
            width: 100%;
            top: 0;
            left: -100%;
            background: linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);
        }
    }
}
</style>
